import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ChevronUp, ChevronDown, X, Sparkles, ExternalLink } from "lucide-react";

/**
 * Phase B4 — Go-live checklist widget.
 *
 * Bottom-right floating card, persistent across all authenticated pages
 * (except kiosk / help / super-admin — gated by OnboardingHost).
 *
 * Two render modes:
 *   • is_complete === true  → "You're live — nice work" pill chip with X.
 *   • is_complete === false → expanded card. Two dismiss affordances:
 *       (a) prominent X in the header (always visible)
 *       (b) "Hide for now" text button at the bottom of the body.
 *     Both fire the same `onDismiss` callback. Each carries
 *     `data-testid="checklist-dismiss"` so the tester's selector
 *     resolves regardless of which surface is shown.
 *
 * Phase B4.1 hardening (after first tester pass) — the previous
 * single-text-link dismiss was a tiny 11px-text element with no clear
 * button affordance, easy to miss in headless runs. Replaced with a
 * proper button + header X.
 */
export default function GoLiveChecklistWidget({ checklist, onDismiss }) {
  // ALL hooks declared before any early return — rules-of-hooks.
  const [collapsed, setCollapsed] = useState(false);
  // Local guard so a double-click during the network round-trip doesn't
  // fire two PATCHes against the same end state.
  const [dismissing, setDismissing] = useState(false);

  const handleDismiss = async (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (dismissing) return;
    setDismissing(true);
    try { await onDismiss?.(); } finally { setDismissing(false); }
  };

  if (!checklist || !checklist.items) return null;

  const { items, completed_count: done, total_count: total, is_complete: isComplete } = checklist;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  if (isComplete) {
    return (
      <div
        className="fixed bottom-4 right-4 z-50 bg-white dark:bg-navy-800 border border-green-300 rounded-full pl-3 pr-1 py-1.5 shadow-xl flex items-center gap-2"
        data-testid="go-live-chip"
      >
        <Sparkles className="w-4 h-4 text-green-600" />
        <span className="text-xs font-semibold text-navy-900 dark:text-white">You're live — nice work</span>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={dismissing}
          className="w-7 h-7 rounded-full flex items-center justify-center text-navy-400 hover:bg-navy-100 dark:hover:bg-navy-700 hover:text-navy-900 disabled:opacity-50"
          aria-label="Hide go-live checklist"
          data-testid="checklist-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-[320px] bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-xl shadow-xl overflow-hidden"
      data-testid="go-live-checklist"
    >
      {/* Header — collapse-toggle on the LEFT, dismiss-X on the RIGHT.
          Splitting them is important: the previous single-button header
          meant any click in the header bar fired collapse(), so the
          dismiss link at the bottom was the only path. Now the X is a
          dedicated button outside the toggle's hit-area. */}
      <div className="flex items-stretch bg-gradient-to-r from-white to-orange-50/50 dark:from-navy-800 dark:to-navy-700 border-b border-navy-100 dark:border-navy-700">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex-1 min-w-0 px-4 py-3 flex items-center justify-between"
          data-testid="go-live-toggle"
          aria-label={collapsed ? "Expand go-live checklist" : "Collapse go-live checklist"}
        >
          <div className="flex items-center gap-2 min-w-0">
            <CheckCircle2 className="w-4 h-4 text-orange-500 shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-navy-900 dark:text-white text-left">Go-live checklist</div>
              <div className="text-[10px] text-navy-500 dark:text-navy-300 text-left">{done} of {total} complete</div>
            </div>
          </div>
          {collapsed
            ? <ChevronUp className="w-4 h-4 text-navy-400 shrink-0 ml-2" />
            : <ChevronDown className="w-4 h-4 text-navy-400 shrink-0 ml-2" />}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={dismissing}
          className="w-10 flex items-center justify-center text-navy-400 hover:bg-navy-100 dark:hover:bg-navy-700 hover:text-orange-600 transition-colors border-l border-navy-100 dark:border-navy-700 disabled:opacity-50"
          aria-label="Hide go-live checklist"
          data-testid="checklist-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {!collapsed && (
        <>
          <div className="px-4 pt-3 pb-1">
            <div className="h-1.5 bg-navy-100 dark:bg-navy-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-orange-500 transition-all duration-300"
                style={{ width: `${pct}%` }}
                data-testid="go-live-progress-bar"
              />
            </div>
          </div>
          <ul className="px-2 py-2 space-y-0.5" data-testid="go-live-items">
            {items.map((it) => (
              <li key={it.id}>
                <Link
                  to={it.route}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors ${
                    it.done
                      ? "text-navy-400 line-through hover:bg-navy-50 dark:hover:bg-navy-700"
                      : "text-navy-700 dark:text-navy-100 hover:bg-navy-50 dark:hover:bg-navy-700"
                  }`}
                  data-testid={`go-live-item-${it.id}`}
                >
                  {it.done ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" data-testid={`go-live-icon-done-${it.id}`} />
                  ) : (
                    <Circle className="w-4 h-4 text-orange-400 shrink-0" data-testid={`go-live-icon-pending-${it.id}`} />
                  )}
                  <span className="flex-1 truncate">{it.label}</span>
                  {it.optional && (
                    <span className="text-[9px] uppercase tracking-wider text-navy-400">opt</span>
                  )}
                  {!it.done && <ExternalLink className="w-3 h-3 text-navy-300" />}
                </Link>
              </li>
            ))}
          </ul>
          {/* Bottom dismiss — kept as a backup affordance for users who
              go looking at the end of the card. Same handler, same
              testid as the header X. */}
          <div className="border-t border-navy-100 dark:border-navy-700 px-3 py-2 flex items-center justify-end">
            <button
              type="button"
              onClick={handleDismiss}
              disabled={dismissing}
              className="text-xs text-navy-500 hover:text-orange-600 font-medium px-2 py-1 rounded-md hover:bg-navy-50 dark:hover:bg-navy-700 disabled:opacity-50"
              aria-label="Hide go-live checklist"
              data-testid="checklist-dismiss"
            >
              Hide for now
            </button>
          </div>
        </>
      )}
    </div>
  );
}
